import { useQuery } from '@tanstack/react-query';
import { useContext } from 'react';
import { useDebounceValue } from 'usehooks-ts';

import { ApplicationContext } from '../../../../../shared/source/context/context';
import { globalSearchSchema, globalSearchTermSchema } from '../../../data-contract/global-search';
import { useGlobalPermissions } from '../global-permissions';

import type { Pathname } from '../../../../../shared/source/http-fetcher/http-fetcher';
import type { GlobalSearch } from '../../../data-contract/global-search';
import type { UseQueryResult } from '@tanstack/react-query';

const debounceSearchQueryMs = 200;
const searchLimitDefault = 4;

export const useGlobalSearchQuery = (
    searchTerm: string,
    excludedSkills: readonly string[],
): UseQueryResult<GlobalSearch> => {
    const { applicationState, validatingCobrainerApiFetcher } = useContext(ApplicationContext);
    const { language } = applicationState;
    const [searchTermDebounced] = useDebounceValue(searchTerm, debounceSearchQueryMs);
    const { data: globalPermissions, isSuccess } = useGlobalPermissions();

    const pathname: Pathname = '/global/search';

    return useQuery({
        enabled:
            isSuccess &&
            globalPermissions.canUserSearch &&
            globalSearchTermSchema.safeParse(searchTermDebounced).success,
        queryFn: async ({ signal }): Promise<GlobalSearch> => {
            const result = await validatingCobrainerApiFetcher.get(globalSearchSchema, {
                pathname,
                requestOptions: {
                    searchParams: {
                        limit: searchLimitDefault,
                        searchTerm: searchTermDebounced,
                        skillIds: excludedSkills.join(','),
                    },
                    signal,
                },
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        queryKey: [pathname, 'GET', searchLimitDefault, searchTermDebounced, excludedSkills, language.code],
    });
};
